/**
 * Virtual MIDI Keyboard Component
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { ChevronUp, ChevronDown, Music } from 'lucide-react';
import { NOTE_NAMES, KEYBOARD_MAP, midiToNoteName } from './MIDIEngine';

const WHITE_KEYS = [0, 2, 4, 5, 7, 9, 11]; // C, D, E, F, G, A, B
const BLACK_KEYS = [1, 3, 6, 8, 10]; // C#, D#, F#, G#, A#

export const VirtualKeyboard = ({
  onNoteOn,
  onNoteOff,
  activeNotes = [],
  octave = 4,
  onOctaveChange,
  isRecording = false,
  keyboardEnabled = true,
}) => {
  const [pressedKeys, setPressedKeys] = useState(new Set());
  const keyboardRef = useRef(null);

  // Base MIDI note for current octave
  const baseNote = (octave + 1) * 12; // C of current octave

  // Handle keyboard input
  useEffect(() => {
    if (!keyboardEnabled) return;

    const handleKeyDown = (e) => {
      if (e.repeat) return;
      const key = e.key.toLowerCase();
      
      if (KEYBOARD_MAP[key] !== undefined) {
        e.preventDefault();
        const note = KEYBOARD_MAP[key] + (octave - 4) * 12;
        if (!pressedKeys.has(note)) {
          setPressedKeys(prev => new Set([...prev, note]));
          onNoteOn?.(note, 100);
        }
      }
    };

    const handleKeyUp = (e) => {
      const key = e.key.toLowerCase();
      
      if (KEYBOARD_MAP[key] !== undefined) {
        e.preventDefault();
        const note = KEYBOARD_MAP[key] + (octave - 4) * 12;
        setPressedKeys(prev => {
          const next = new Set(prev);
          next.delete(note);
          return next;
        });
        onNoteOff?.(note);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [octave, onNoteOn, onNoteOff, pressedKeys, keyboardEnabled]);

  // Handle mouse/touch events on keys
  const handleKeyStart = useCallback((note, e) => {
    e.preventDefault();
    // Calculate velocity from Y position (higher = louder)
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY || e.touches?.[0]?.clientY || rect.top;
    const relY = (y - rect.top) / rect.height;
    const velocity = Math.round(60 + relY * 67); // 60-127
    
    setPressedKeys(prev => new Set([...prev, note]));
    onNoteOn?.(note, velocity);
  }, [onNoteOn]);

  const handleKeyEnd = useCallback((note) => {
    setPressedKeys(prev => {
      const next = new Set(prev);
      next.delete(note);
      return next;
    });
    onNoteOff?.(note);
  }, [onNoteOff]);

  // Render a single octave
  const renderOctave = (octaveNum, index) => {
    const octaveBase = (octaveNum + 1) * 12;

    return (
      <div key={octaveNum} className="relative flex">
        {/* White keys */}
        {WHITE_KEYS.map((noteOffset, i) => {
          const note = octaveBase + noteOffset;
          const isActive = activeNotes.includes(note) || pressedKeys.has(note);
          const noteName = NOTE_NAMES[noteOffset];
          
          return (
            <motion.div
              key={`white-${note}`}
              className={`relative w-10 h-32 border border-zinc-700 rounded-b-lg cursor-pointer select-none
                ${isActive 
                  ? 'bg-indigo-500 shadow-lg shadow-indigo-500/50' 
                  : 'bg-zinc-100 hover:bg-zinc-200'
                }
                ${isRecording ? 'border-red-500/50' : ''}
              `}
              whileTap={{ scale: 0.98 }}
              onMouseDown={(e) => handleKeyStart(note, e)}
              onMouseUp={() => handleKeyEnd(note)}
              onMouseLeave={() => pressedKeys.has(note) && handleKeyEnd(note)}
              onTouchStart={(e) => handleKeyStart(note, e)}
              onTouchEnd={() => handleKeyEnd(note)}
              data-testid={`piano-key-${note}`}
            >
              <span className={`absolute bottom-2 left-1/2 -translate-x-1/2 text-xs font-mono
                ${isActive ? 'text-white' : 'text-zinc-500'}
              `}>
                {noteName}{octaveNum}
              </span>
            </motion.div>
          );
        })}
        
        {/* Black keys */}
        <div className="absolute top-0 left-0 right-0 flex">
          {WHITE_KEYS.map((noteOffset, i) => {
            // Check if there's a black key after this white key
            const blackNoteOffset = noteOffset + 1;
            if (!BLACK_KEYS.includes(blackNoteOffset)) return null;
            
            const note = octaveBase + blackNoteOffset;
            const isActive = activeNotes.includes(note) || pressedKeys.has(note);
            
            // Position black key
            const leftOffset = (i + 1) * 40 - 12; // 40px per white key, -12 for centering
            
            return (
              <motion.div
                key={`black-${note}`}
                className={`absolute w-6 h-20 rounded-b-lg cursor-pointer select-none z-10
                  ${isActive 
                    ? 'bg-indigo-600 shadow-lg shadow-indigo-600/50' 
                    : 'bg-zinc-900 hover:bg-zinc-800'
                  }
                  ${isRecording ? 'border border-red-500/50' : ''}
                `}
                style={{ left: `${leftOffset}px` }}
                whileTap={{ scale: 0.98 }}
                onMouseDown={(e) => handleKeyStart(note, e)}
                onMouseUp={() => handleKeyEnd(note)}
                onMouseLeave={() => pressedKeys.has(note) && handleKeyEnd(note)}
                onTouchStart={(e) => handleKeyStart(note, e)}
                onTouchEnd={() => handleKeyEnd(note)}
                data-testid={`piano-key-${note}`}
              />
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-zinc-900/95 backdrop-blur-xl border border-zinc-800 rounded-xl p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Music className="w-4 h-4 text-indigo-400" />
          <span className="text-sm font-medium">Virtual Keyboard</span>
          {isRecording && (
            <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full animate-pulse">
              REC
            </span>
          )}
        </div>
        
        {/* Octave Controls */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">Octave</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOctaveChange?.(Math.max(1, octave - 1))}
            disabled={octave <= 1}
            className="h-7 w-7 p-0 border-zinc-700"
            data-testid="octave-down"
          >
            <ChevronDown className="w-4 h-4" />
          </Button>
          <span className="w-6 text-center font-mono text-sm">{octave}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOctaveChange?.(Math.min(7, octave + 1))}
            disabled={octave >= 7}
            className="h-7 w-7 p-0 border-zinc-700"
            data-testid="octave-up"
          >
            <ChevronUp className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Keyboard */}
      <div 
        ref={keyboardRef}
        className="flex overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-zinc-700"
        data-testid="virtual-keyboard"
      >
        {/* Render 2 octaves */}
        {[octave - 1, octave].map((oct, i) => renderOctave(oct, i))}
      </div>
      
      {/* Keyboard Hints */}
      <div className="mt-3 text-xs text-zinc-500 flex items-center gap-4">
        <span>Computer keys: A-L = C-E, W,E,T,Y,U,O,P = sharps</span>
      </div>
    </div>
  );
};

export default VirtualKeyboard;
